import { array, boolean, object, string } from "yup";
import { defaultContextFields, hospital, isoDateString, nonEmptyString, optionalCheckbox, } from "../../shared.js";
export const soadContactStatuses = ["contacted", "not-contacted"];
const PERCENTAGE_OF_BNF_MAXIMUM_ERROR = "Please enter the total percentage of the BNF maximum dosage, over 100 (e.g. 150)";
export const drugEntrySchema = object({
    category: string().required("Please select drug category"),
    categoryText: string().nullable().optional(),
    drugNames: nonEmptyString.required("Please enter the name of the drug"),
    routeOfAdministration: string().required("Please select the route of administration"),
    routeOfAdministrationText: string().nullable().optional(),
    regularity: string().required("Please select the regularity"),
    regularityText: string().nullable().optional(),
    isAboveBnfMaximum: boolean()
        .nullable()
        .required("Please confirm if above the BNF maximum dosage"),
    percentageOfBnfMaximum: nonEmptyString.when("isAboveBnfMaximum", {
        is: true,
        then: (schema) => schema
            .required(PERCENTAGE_OF_BNF_MAXIMUM_ERROR)
            .matches(/^\d+$/, PERCENTAGE_OF_BNF_MAXIMUM_ERROR)
            .test("above-bnf-maximum", PERCENTAGE_OF_BNF_MAXIMUM_ERROR, (value) => value == null || Number(value) > 100),
        otherwise: (schema) => schema.nullable().optional(),
    }),
});
export const s62Part1DataSchema = object({
    ...defaultContextFields({ approvedClinicianRequired: true }),
    hospital,
    sectionSubjectTo: string().required("Please state section patient is subject to"),
    sectionSubjectToText: string().nullable().optional(),
    reasonForS62: array()
        .of(string())
        .min(1, "Please provide additional context on the reason for Section 62 use today")
        .required("Please provide additional context on the reason for Section 62 use today")
        .default([]),
    reasonForS62Text: array().of(string()).nullable().optional(),
    // Treatment type — a multi-select; at least one must be chosen. The
    // "at least one" test lives on treatmentEct (the last option) so it runs
    // once both booleans are known, mirroring the criteria pattern.
    treatmentMedication: optionalCheckbox,
    treatmentEct: optionalCheckbox.test("at-least-one-treatment-selected", "Please select at least one treatment type", (_, ctx) => {
        const parent = ctx.parent;
        return parent.treatmentMedication || parent.treatmentEct;
    }),
    // Urgent-treatment criteria for medication (all four available). At least
    // one must be selected when medication is a chosen treatment type.
    medicationCriteriaA: optionalCheckbox,
    medicationCriteriaB: optionalCheckbox,
    medicationCriteriaC: optionalCheckbox,
    medicationCriteriaD: optionalCheckbox.test("at-least-one-medication-criteria-selected", "Please select at least one option", (_, ctx) => {
        const parent = ctx.parent;
        if (!parent.treatmentMedication)
            return true;
        return (parent.medicationCriteriaA ||
            parent.medicationCriteriaB ||
            parent.medicationCriteriaC ||
            parent.medicationCriteriaD);
    }),
    // Urgent-treatment criteria for ECT. Only (a) and (b) authorise ECT under
    // Section 62, and the "at least one" test requires one of them. Criteria (c)
    // and (d) are selectable and not rejected: the form warns that they do not
    // authorise ECT but does not block the clinician from recording them, so we
    // deliberately avoid enforcing clinical correctness here.
    ectCriteriaA: optionalCheckbox,
    ectCriteriaB: optionalCheckbox.test("at-least-one-ect-criteria-selected", "Please select at least one option", (_, ctx) => {
        const parent = ctx.parent;
        if (!parent.treatmentEct)
            return true;
        return parent.ectCriteriaA || parent.ectCriteriaB;
    }),
    ectCriteriaC: optionalCheckbox,
    ectCriteriaD: optionalCheckbox,
    maxEctTreatments: string()
        .nullable()
        .when("treatmentEct", {
        is: true,
        then: (schema) => schema.required("Please provide the maximum number of ECT treatments"),
        otherwise: (schema) => schema.nullable().optional(),
    }),
    maxEctTreatmentsText: string().nullable().optional(),
    drugEntries: array()
        .of(drugEntrySchema)
        .when("treatmentMedication", {
        is: true,
        then: (schema) => schema
            .min(1, "Please add medication details")
            .required("Please add medication details"),
        otherwise: (schema) => schema.nullable().optional(),
    })
        .default([]),
    isLongTermTreatment: boolean()
        .nullable()
        .required("Please confirm if you expect this treatment to continue long term"),
    soadContactStatus: string()
        .oneOf(soadContactStatuses)
        .nullable()
        .when("isLongTermTreatment", {
        is: true,
        then: (schema) => schema.required("Please confirm which answer applies"),
        otherwise: (schema) => schema.nullable().optional(),
    }),
    soadContactDate: isoDateString.when("soadContactStatus", {
        is: "contacted",
        then: (schema) => schema.required("Please provide the date you made a request to the CQC for a second opinion (SOAD)"),
        otherwise: (schema) => schema.nullable().optional(),
    }),
    soadRequestNumber: nonEmptyString.nullable().optional(),
    soadNotContactedReasons: nonEmptyString.nullable().when("soadContactStatus", {
        is: "not-contacted",
        then: (schema) => schema.required("Please describe the exceptional reasons for not contacting a SOAD and when you expect to do so"),
        otherwise: (schema) => schema.nullable().optional(),
    }),
});
export const exampleS62Data = [
    {
        data: {
            user: {
                name: "Dr Jane Smith",
                email: "jane.smith@nhs.net",
                address: "St. Thomas' Hospital, Westminster Bridge Road",
                postalCode: "SE1 7EH",
                isConfirmed: true,
                isApprovedClinician: true,
                isSection12Approved: false,
                isGuest: false,
            },
            patient: {
                name: "John Patient",
                address: "123 Patient Street, London",
                postalCode: "SW1A 1AA",
                dateOfBirth: "1980-06-15",
                nhsNumber: "9999999999",
            },
            hospital: {
                name: "St. Thomas' Hospital",
                address: "Westminster Bridge Road",
                postalCode: "SE1 7EH",
                isConfirmed: true,
            },
            sectionSubjectTo: "section3",
            sectionSubjectToText: "Section 3: Admission for treatment",
            reasonForS62: ["threeMonthsPassed"],
            reasonForS62Text: ["3 months has passed"],
            treatmentMedication: true,
            treatmentEct: true,
            medicationCriteriaA: false,
            medicationCriteriaB: true,
            medicationCriteriaC: true,
            medicationCriteriaD: false,
            ectCriteriaA: false,
            ectCriteriaB: true,
            ectCriteriaC: false,
            ectCriteriaD: false,
            maxEctTreatments: "2",
            maxEctTreatmentsText: "2",
            drugEntries: [
                {
                    category: "drugsUsedInPsychoses",
                    categoryText: "Drugs used in psychoses and related disorders",
                    drugNames: "Haloperidol",
                    routeOfAdministration: "oral",
                    routeOfAdministrationText: "Oral",
                    regularity: "regular",
                    regularityText: "Regular",
                    isAboveBnfMaximum: false,
                    percentageOfBnfMaximum: null,
                },
            ],
            isLongTermTreatment: true,
            soadContactStatus: "contacted",
            soadContactDate: "2026-01-15",
            soadRequestNumber: "SOAD-2026-0042",
            soadNotContactedReasons: null,
        },
    },
];
//# sourceMappingURL=s62.js.map